import "@/App.css";
import { BrowserRouter, Routes, Route, Navigate } from "react-router-dom";
import Landing from "@/pages/Landing";
import Login from "@/pages/auth/Login";
import Register from "@/pages/auth/Register";
import ForgotPassword from "@/pages/auth/ForgotPassword";
import ResetPassword from "@/pages/auth/ResetPassword";
import Dashboard from "@/pages/Dashboard";
import WalletsPage from "@/pages/app/Wallets";
import ActivityPage from "@/pages/app/Activity";
import SettingsPage from "@/pages/app/Settings";
import KycPage from "@/pages/app/Kyc";
import DigioKycCallback from "@/pages/app/DigioKycCallback";
import ProfilePage from "@/pages/app/Profile";
import OtcPage from "@/pages/app/Otc";
import SupportPage from "@/pages/app/Support";
import MyRewardsPage from "@/pages/app/MyRewards";
import AdminLogin from "@/pages/admin/AdminLogin";
import AdminDashboard from "@/pages/admin/Dashboard";
import Market from "@/pages/Market";
import Trade from "@/pages/Trade";
import FuturesTrade from "@/pages/FuturesTrade";
import About from "@/pages/About";
import Blog from "@/pages/Blog";
import BlogPost from "@/pages/BlogPost";
import Terms from "@/pages/Terms";
import Privacy from "@/pages/Privacy";
import Fees from "@/pages/Fees";
import DownloadApp from "@/pages/DownloadApp";
import AdminUsers from "@/pages/admin/Users";
import AdminUserDetail from "@/pages/admin/UserDetail";
import AdminAccessControl from "@/pages/admin/AccessControl";
import AdminSpotTrading from "@/pages/admin/SpotTrading";
import AdminFutures from "@/pages/admin/AdminFutures";
import AdminWalletManagement from "@/pages/admin/WalletManagement";
import AdminWithdrawalApprovals from "@/pages/admin/WithdrawalApprovals";
import AdminWalletWhitelistApprovals from "@/pages/admin/WalletWhitelistApprovals";
import AdminDepositWhitelistApprovals from "@/pages/admin/DepositWhitelistApprovals";
import AdminINRPayments from "@/pages/admin/INRPayments";
import AdminFeeManagement from "@/pages/admin/FeeManagement";
import AdminRiskManagement from "@/pages/admin/RiskManagement";
import AdminFiuAml from "@/pages/admin/FiuAml";
import AdminMarketSurveillance from "@/pages/admin/MarketSurveillance";
import AdminRiskCompliance from "@/pages/admin/RiskCompliance";

import AdminReports from "@/pages/admin/ReportsAnalytics";
import AdminCms from "@/pages/admin/CmsAdmin";
import AdminBlog from "@/pages/admin/AdminBlog";
import AdminSecurity from "@/pages/admin/SecurityControls";
import AdminSmsDltSettings from "@/pages/admin/SmsDltSettings";
import AdminTax from "@/pages/admin/TaxationGst";
import AdminTdsSettings from "@/pages/admin/TdsSettings";
import AdminTdsTransactions from "@/pages/admin/TdsTransactions";
import AdminTdsReports from "@/pages/admin/TdsReports";
import AdminSupport from "@/pages/admin/SupportAdmin";
import AdminKyc from "@/pages/admin/KycReview";
import AdminSignzySettings from "@/pages/admin/SignzySettings";
import AdminOtc from "@/pages/admin/OtcDesk";
import AdminRewardsBonuses from "@/pages/admin/RewardsBonuses";
import AdminAppReleases from "@/pages/admin/AppReleases";
import ComingSoon from "@/pages/ComingSoon";
import LaunchGateGuard from "@/components/LaunchGateGuard";
import AdminLaunchGate from "@/pages/admin/AdminLaunchGate";
import RequireAuth from "@/components/RequireAuth";
import { AuthProvider } from "@/lib/auth";
import { AdminAuthProvider } from "@/lib/adminAuth";

const guarded = (el) => <RequireAuth>{el}</RequireAuth>;

function App() {
  return (
    <div className="App">
      <AuthProvider>
        <AdminAuthProvider>
          <BrowserRouter>
          <LaunchGateGuard>
          <Routes>
            {/* Public */}
            <Route path="/" element={<Landing />} />
            <Route path="/markets" element={<Market />} />
            <Route path="/trade/spot/:pair" element={<Trade />} />
            <Route path="/trade/futures/:pair" element={guarded(<FuturesTrade />)} />
            <Route path="/trade/futures" element={guarded(<Navigate to="/trade/futures/BTC-USDT" replace />)} />
            <Route path="/trade" element={<Navigate to="/markets" replace />} />
            <Route path="/login" element={<Login />} />
            <Route path="/register" element={<Register />} />
            <Route path="/forgot-password" element={<ForgotPassword />} />
            <Route path="/reset-password" element={<ResetPassword />} />

            {/* App (auth required — must have email + mobile verified) */}
            <Route path="/dashboard" element={guarded(<Dashboard />)} />
            <Route path="/wallets" element={guarded(<WalletsPage />)} />
            <Route path="/activity" element={guarded(<ActivityPage />)} />
            <Route path="/settings" element={guarded(<SettingsPage />)} />
            <Route path="/kyc" element={guarded(<KycPage />)} />
            <Route path="/kyc/digio/callback" element={guarded(<DigioKycCallback />)} />
            <Route path="/profile" element={guarded(<ProfilePage />)} />
            <Route path="/otc" element={guarded(<OtcPage />)} />
            <Route path="/support" element={guarded(<SupportPage />)} />
            <Route path="/rewards" element={guarded(<MyRewardsPage />)} />

            {/* Hidden product routes redirect away — screens stay in the codebase but are not reachable. */}
            <Route path="/p2p/*" element={<Navigate to="/markets" replace />} />

            {/* Admin */}
            <Route path="/admin/login" element={<AdminLogin />} />
            <Route path="/admin" element={<Navigate to="/admin/dashboard" replace />} />
            <Route path="/admin/dashboard" element={<AdminDashboard />} />
            <Route path="/admin/users" element={<AdminUsers />} />
            <Route path="/admin/users/:id" element={<AdminUserDetail />} />
            <Route path="/admin/access-control" element={<AdminAccessControl />} />
            <Route path="/admin/spot" element={<AdminSpotTrading />} />
            <Route path="/admin/futures" element={<AdminFutures />} />
            <Route path="/admin/wallet" element={<AdminWalletManagement />} />
            <Route path="/admin/wallet/approvals" element={<AdminWithdrawalApprovals />} />
            <Route path="/admin/wallet/whitelist-approvals" element={<AdminWalletWhitelistApprovals />} />
            <Route path="/admin/wallet/deposit-whitelist-approvals" element={<AdminDepositWhitelistApprovals />} />
            <Route path="/admin/p2p/*" element={<Navigate to="/admin/dashboard" replace />} />
            <Route path="/admin/inr" element={<AdminINRPayments />} />
            <Route path="/admin/fees" element={<AdminFeeManagement />} />
            <Route path="/admin/risk" element={<AdminRiskManagement />} />
            <Route path="/admin/aml" element={<AdminFiuAml />} />
            <Route path="/admin/surveillance" element={<AdminMarketSurveillance />} />
            <Route path="/admin/compliance" element={<AdminRiskCompliance />} />

            <Route path="/admin/reports" element={<AdminReports />} />
            <Route path="/admin/cms" element={<AdminCms />} />
            <Route path="/admin/launch-gate" element={<AdminLaunchGate />} />
            <Route path="/admin/blog" element={<AdminBlog />} />
            <Route path="/admin/security" element={<AdminSecurity />} />
            <Route path="/admin/security/sms-dlt" element={<AdminSmsDltSettings />} />
            <Route path="/admin/security/ip-whitelist" element={<Navigate to="/admin/access-control?tab=ip_logs" replace />} />
            <Route path="/admin/security/access-logs" element={<Navigate to="/admin/access-control?tab=ip_logs" replace />} />
            <Route path="/admin/tax" element={<AdminTax />} />
            <Route path="/admin/finance/tds" element={<AdminTdsSettings />} />
            <Route path="/admin/finance/tds/transactions" element={<AdminTdsTransactions />} />
            <Route path="/admin/reports/tds" element={<AdminTdsReports />} />
            <Route path="/admin/support" element={<AdminSupport />} />
            <Route path="/admin/kyc" element={<AdminKyc />} />
            <Route path="/admin/kyc/signzy" element={<AdminSignzySettings />} />
            <Route path="/admin/otc" element={<AdminOtc />} />
            <Route path="/admin/marketing/rewards" element={<AdminRewardsBonuses />} />
            <Route path="/admin/marketing/app-releases" element={<AdminAppReleases />} />

            {/* Coming soon */}
            <Route
              path="/transfer"
              element={
                <ComingSoon
                  title="Transfer — coming soon"
                  description="Instant INR & crypto transfers are on the way."
                />
              }
            />
            <Route path="/blog" element={<Blog />} />
            <Route path="/blog/:slug" element={<BlogPost />} />
            <Route path="/about" element={<About />} />
            <Route path="/download" element={<DownloadApp />} />
            <Route path="/terms" element={<Terms />} />
            <Route path="/privacy" element={<Privacy />} />
            <Route path="/fees" element={<Fees />} />
          </Routes>
          </LaunchGateGuard>
        </BrowserRouter>
        </AdminAuthProvider>
      </AuthProvider>
    </div>
  );
}

export default App;
